/**
 * components/print/InvoiceCabang.js
 * Invoice Cabang — the cabang-anchored sales document (PRD §5.3). Portal-based, same pattern
 * `InvoicePusat.js` established (`createPortal(..., document.body)`, imports
 * `printFoundation.css`) — renders on `.print-doc--cabang` (the short 9.5in×5.5in page), NOT
 * `.print-doc--pusat`; this document was built cabang-sized from the start, unlike the two
 * pusat documents that had to be corrected onto it in T2c-2-fix.
 *
 * **No `company` prop** — the top-left letterhead is just `cabangName`, a plain string prop
 * this component prints as-is, a SEPARATE entity from the pusat company's full letterhead block
 * Invoice Pusat/Checklist print (printing the pusat company's name onto a cabang document would
 * be the wrong entity — confirmed with the client via a real reference photo before this was
 * built, not guessed). **This component itself has no idea where `cabangName` comes from** —
 * the real source is the call site (`Penjualan.js`/`PiutangUtang.js`), which resolves it
 * PER-TRANSACTION via `resolveBranchName(branches, txn.branchId) || undefined`; this file's own
 * `cabangName = DEFAULT_CABANG_NAME` default parameter (`utils/company.js`) only fires when
 * that resolves to nothing (an old transaction predating branches, a since-deleted branch, or a
 * caller that passes no prop at all, e.g. a direct-mount test). **NOT** `settings.cabangName` —
 * that stored field is vestigial data nothing reads (see the root doc's `settings` entry).
 *
 * **Pagination is the OPPOSITE of Invoice Pusat's** (PRD §5.3 vs §5.2): items run with
 * continuous numbering and arabic `HAL:{page}/{pageCount}` markers (matching the Checklist's
 * own marker style, not Invoice Pusat's roman `Hal I`/`Hal II`) across every page — there is NO
 * per-page carry-in row and NO running subtotal. A single TOTAL row prints ONLY on the LAST
 * page. This needed NO change to `utils/pagination.js`'s `paginateLines` — `carryIn` is simply
 * never read here (the same "unused field" pattern `Checklist.js` already established), and the
 * TOTAL row's JSX is gated on `page.pageNumber === pageCount` instead of always rendering.
 *
 * **No footer at all** — confirmed via a real reference photo (unlike Invoice Pusat's own
 * bordered Diterima,/Hormat Kami, box): the table ends, the final page's TOTAL row prints, and
 * the document stops there. Don't add one back without new photo evidence.
 *
 * COLY is captured per-line via `InvoiceCabangDialog.js` (mirroring `ChecklistDialog.js`'s
 * per-line free-text pattern, not `SuratJalanDialog`'s row-editor — different data shape).
 *
 * **cabang-fix session — header column swap**: `NOTA NO`/`HAL:x/y` moved from the RIGHT header
 * column into the LEFT column (stacked under the branch title); the right column now holds only
 * the date + buyer block. Confirmed against 2 real reference photos (single-item + multi-page
 * Wijaya Motor samples) — the previous layout had NOTA NO/HAL mixed into the same column as the
 * buyer block, which didn't match either photo. `.print-doc__hal--left` (printFoundation.css)
 * left-aligns the marker in its new column without touching the shared base rule, which
 * `InvoicePusat.js` still depends on for its own right-column marker.
 *
 * **Address overrides for printing**: a screen-only editor sits to the LEFT of the document,
 * side by side (`.print-doc-overlay__body` — see InvoicePusat.js's own header comment for the
 * full layout story, incl. the narrow-viewport stacked fallback). Lets staff correct the buyer
 * address for THIS print only, defaulting from the
 * FROZEN `txn.buyerBlock.address` (never a live Contact lookup — see InvoicePusat.js's own
 * header comment for the full reasoning) and persisting to
 * `txn.addressOverrides.invoiceCabang` only on Cetak. A blank field blocks Cetak.
 *
 * **The cabang @page geometry fix**: `.print-doc--cabang` (below) now sits inside a page
 * declared at the SAME 8.5in×11in geometry as `.print-doc--pusat` — a real physical print of
 * Surat Jalan Pusat overflowed onto a second sheet on the real EPSON LX-300+II, traced to the
 * driver picking the wrong paper preset for a landscape-shaped `8.5in×5.5in` page request; this
 * document shares the exact same `@page cabang` rule and is fixed by the same change. This
 * document's OWN layout/rows/`ROWS_PER_PAGE_CABANG` are UNCHANGED — see `printFoundation.css`'s
 * own header comment (last dated paragraph) for the full investigation.
 */
import React, { useState } from "react";
import { createPortal } from "react-dom";
import "./printFoundation.css";
import { paginateLines, NBSP, PRINT_SETTINGS_REMINDER_CABANG } from "../../utils/pagination";
import { DEFAULT_CABANG_NAME } from "../../utils/company";
import { fmtQty, fmtDateDMY } from "../../utils/idGenerators";
import AddressOverrideField from "./AddressOverrideField";

// Measured via a real headless-Chrome print-media render against the cabang page's known
// 5.0in content height (422.4px inner, same arithmetic base as Surat Jalan Pusat/Checklist):
// header+hr overhead ~130px (this document's own header — branch name, date, NOTA NO, buyer
// block, plus the HAL line when multi-page) + 26.98px thead, leaving ~265px for rows — BUT the
// naive floor of that (9) turned out unsafe once measured for real: the TOTAL row only ever
// appears on a page that's ALSO carrying the multi-page HAL line (any page but the sole page of
// a single-page document has slack to spare, since it never shows HAL), and a full 9-row LAST
// page + TOTAL + HAL genuinely overflowed onto a 3rd physical page in that real render. `8` was
// then confirmed via page.pdf() against the true worst case — a full 8-row LAST page carrying
// BOTH the TOTAL row and the HAL marker — landing at exactly 480px (zero residual gap, zero
// overflow); 9 real lines (a thin 2-page split) also confirmed clean.
//
// **Print-width foundational correction — re-measured, LOWERED from `8` to `7`.** The
// `.print-doc--cabang` content width dropped 8.7in→8.2in (see printFoundation.css's own
// header comment) once the app's foundational 9.5in "printable width" assumption was corrected
// to the real, ruler-measured 8.5in — the Nama Barang column shrank from 2.6in to 2.1in as a
// direct result. Real `page.pdf()` re-measurement of the EXACT same worst case (a full 8-row
// LAST page carrying the TOTAL row, the HAL marker, and one realistic wrapped name — e.g. a
// long real part name) now overflows by a real, measured 2.375px (page box grows to 482.375px
// against the fixed 480px physical limit) — a small but genuine regression, not a rounding
// artifact, confirmed via `getBoundingClientRect` on the actual `.print-page` box. `7` restores
// a real, confirmed non-negative margin at the same worst case (1 wrapped name on a full last
// page with TOTAL+HAL) — re-verify via `page.pdf()` before changing this value again.
//
// **The row-budget slack investigation — RAISED again, `7` → `9`.** Real physical prints
// showed visible unused space below the table even at `7`, confirmed by real measurement: a
// fresh `page.pdf()`/`getBoundingClientRect()` pass (temporary `puppeteer-core`+`pdf-lib`,
// installed `--no-save`, removed after) found the genuine worst case — a full last page
// carrying the real 35-character catalog-max part name (which DOES wrap at this column's real
// 2.1in width, confirmed both by direct measurement and by progressive truncation: the wrap
// threshold on this column is exactly **24 characters** — 23 fits one line, 24 is the first
// length that wraps), the TOTAL row, and the HAL marker — landing at **39.4px of real margin**
// at `N=9`, not the razor-thin ~2px this constant's own history has already learned to avoid.
// A real 2-simultaneous-wrap scenario was also checked and still clears the page with **18.9px**
// to spare at `N=9` — and this is no longer a purely hypothetical stress case: a full scan of
// the client's real June 2026 catalog (1,179 unique item names) found **165 names (14.0%)**
// exceed the 24-character wrap threshold on this column, so two such names landing on one
// page's last 9 rows is a real, plausible scenario this margin needs to (and does) survive.
// `10` was also measured and rejected — its 1-wrap margin (12.4px) is thin and it does not
// survive a 2-wrap page at all. **Still pending physical confirmation on the real EPSON
// LX-300+II** — re-verify via a real print before changing this value again, same as every
// prior retuning here.
const ROWS_PER_PAGE_CABANG = 9;

export default function InvoiceCabang({ txn, items = [], cabangName = DEFAULT_CABANG_NAME, onClose, onSaveAddressOverride }) {
  const byCode = new Map(items.map((it) => [it.internalCode, it]));
  const { pages, pageCount } = paginateLines(txn.lines || [], ROWS_PER_PAGE_CABANG);
  const savedOverride = txn.addressOverrides?.invoiceCabang;
  const [buyerAddressOverride, setBuyerAddressOverride] = useState(
    () => savedOverride?.buyerAddress ?? (txn.buyerBlock?.address || "")
  );
  const buyer = txn.buyerBlock || { name: "", address: "" };
  const multiPage = pageCount > 1;
  const canPrint = buyerAddressOverride.trim() !== "";
  const handleCetak = () => {
    if (!canPrint) return;
    onSaveAddressOverride?.(txn.id, "invoiceCabang", { buyerAddress: buyerAddressOverride.trim() });
    window.print();
  };

  return createPortal(
    <div className="print-doc-overlay">
      <div className="print-doc-overlay__actions">
        <span className="print-doc-overlay__note">Invoice Cabang — {txn.invoiceNumber || txn.id}</span>
        <span className="print-doc-overlay__reminder">{PRINT_SETTINGS_REMINDER_CABANG}</span>
        <button type="button" className="btn" onClick={onClose}>Tutup</button>
        <button type="button" className="btn btn-primary" onClick={handleCetak} disabled={!canPrint}>Cetak</button>
      </div>

      <div className="print-doc-overlay__body">
        <div className="print-doc-overlay__override-panel">
          <AddressOverrideField label="Alamat Pembeli" value={buyerAddressOverride} onChange={setBuyerAddressOverride} />
        </div>

        <div className="print-doc print-doc--cabang">
        {pages.map((page) => {
          const isLastPage = page.pageNumber === pageCount;
          const fillerCount = Math.max(0, ROWS_PER_PAGE_CABANG - page.lines.length);
          return (
            <div className="print-page" key={page.pageNumber}>
              <div className="print-doc__header">
                <div className="print-doc__header-col">
                  <div className="print-doc__title">{cabangName}</div>
                  <div className="print-doc__header-invoice">
                    <span className="print-doc__label">NOTA NO : </span>{txn.invoiceNumber}
                  </div>
                  {multiPage && (
                    <div className="print-doc__hal print-doc__hal--left">HAL:{page.pageNumber}/{pageCount}</div>
                  )}
                </div>
                <div className="print-doc__header-col print-doc__header-col--right">
                  <div>Jakarta, {fmtDateDMY(txn.date)}</div>
                  <div>{buyer.name}</div>
                  {buyerAddressOverride.split("\n").map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              </div>

              <hr className="print-doc__hr" />

              <div className="print-doc__table-area">
                <table className="print-doc__table">
                  <colgroup>
                    <col style={{ width: "0.4in" }} />
                    <col style={{ width: "1.3in" }} />
                    <col />
                    <col style={{ width: "0.9in" }} />
                    <col style={{ width: "1.0in" }} />
                    <col style={{ width: "1.0in" }} />
                    <col style={{ width: "0.7in" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>No. Barang</th>
                      <th>Nama Barang</th>
                      <th className="num">Qty</th>
                      <th className="num">U/Price</th>
                      <th className="num">Total</th>
                      <th>Coly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.lines.map((l, i) => {
                      const rowNo = (page.pageNumber - 1) * ROWS_PER_PAGE_CABANG + i + 1;
                      const name = l.name || byCode.get(l.internalCode)?.name || l.internalCode;
                      return (
                        <tr key={i}>
                          <td>{rowNo}</td>
                          <td>{l.internalCode}</td>
                          <td className="wrap">{name}</td>
                          <td className="num">{fmtQty(l.qty)} PCS</td>
                          <td className="num">{fmtQty(l.unitPrice)}</td>
                          <td className="num">{fmtQty(l.qty * l.unitPrice)}</td>
                          <td>{l.collyNo || ""}</td>
                        </tr>
                      );
                    })}
                    {Array.from({ length: fillerCount }).map((_, i) => (
                      <tr className="print-doc__filler" key={`filler-${i}`}>
                        <td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td>
                        <td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td>
                      </tr>
                    ))}
                    {isLastPage && (
                      <tr className="print-doc__total-row">
                        <td colSpan={5} style={{ textAlign: "right" }}>TOTAL</td>
                        <td className="num">{fmtQty(page.pageTotal)}</td>
                        <td>{NBSP}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>,
    document.body
  );
}
